import React, { useEffect, useRef } from 'react';
import { PlusSquare, Share, Smartphone, X } from 'lucide-react';
import { STUDIO_COPY } from '../config';
import type { StudioInstallPlatform } from '../utils/studioInstallPlatform';

type StudioMobileAppModalProps = {
  open: boolean;
  onClose: () => void;
  platform: StudioInstallPlatform;
  canPromptInstall: boolean;
  promptInstall: () => Promise<boolean>;
  onInstalled: () => void;
};

const StudioMobileAppModal: React.FC<StudioMobileAppModalProps> = ({
  open,
  onClose,
  platform,
  canPromptInstall,
  promptInstall,
  onInstalled,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleInstall = async () => {
    const accepted = await promptInstall();
    if (accepted) onInstalled();
  };

  return (
    <div
      className="studio-mobile-app-modal-backdrop"
      role="presentation"
      onClick={onClose}
      data-testid="studio-mobile-app-modal-backdrop"
    >
      <div
        ref={dialogRef}
        className="studio-mobile-app-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="studio-mobile-app-modal-title"
        data-testid="studio-mobile-app-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="studio-mobile-app-modal-header">
          <h2 id="studio-mobile-app-modal-title" className="studio-mobile-app-modal-title">
            {STUDIO_COPY.mobileAppModalTitle}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="studio-mobile-app-modal-close"
            onClick={onClose}
            aria-label={STUDIO_COPY.mobileAppCloseModal}
            data-testid="studio-mobile-app-modal-close"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {platform === 'android' || canPromptInstall ? (
          <div className="studio-mobile-app-modal-body" data-testid="studio-mobile-app-android">
            <p className="studio-mobile-app-modal-lead">{STUDIO_COPY.mobileAppBannerBody}</p>
            <button
              type="button"
              className="studio-btn-primary studio-mobile-app-modal-install"
              onClick={() => void handleInstall()}
              data-testid="studio-mobile-app-install"
            >
              {STUDIO_COPY.mobileAppInstall}
            </button>
          </div>
        ) : null}

        {platform === 'ios-safari' ? (
          <ol
            className="studio-mobile-app-ios-steps"
            data-testid="studio-mobile-app-ios-safari"
          >
            <li className="studio-mobile-app-ios-step" data-testid="studio-mobile-app-ios-share">
              <span className="studio-mobile-app-ios-num" aria-hidden>
                1
              </span>
              <span className="studio-mobile-app-ios-glyph studio-mobile-app-ios-glyph--share">
                <Share className="h-8 w-8" aria-hidden />
              </span>
              <span>
                <strong>{STUDIO_COPY.mobileAppIosShareStep}</strong>
                <span className="studio-mobile-app-ios-hint">
                  {STUDIO_COPY.mobileAppIosShareHint}
                </span>
              </span>
            </li>
            <li className="studio-mobile-app-ios-step">
              <span className="studio-mobile-app-ios-num" aria-hidden>
                2
              </span>
              <span className="studio-mobile-app-ios-glyph">
                <PlusSquare className="h-7 w-7" aria-hidden />
              </span>
              <strong>{STUDIO_COPY.mobileAppIosAddStep}</strong>
            </li>
            <li className="studio-mobile-app-ios-step">
              <span className="studio-mobile-app-ios-num" aria-hidden>
                3
              </span>
              <span className="studio-mobile-app-ios-glyph">
                <Smartphone className="h-7 w-7" aria-hidden />
              </span>
              <strong>{STUDIO_COPY.mobileAppIosConfirmStep}</strong>
            </li>
          </ol>
        ) : null}

        {platform === 'ios-other' ? (
          <p
            className="studio-mobile-app-ios-safari-note"
            data-testid="studio-mobile-app-ios-other"
          >
            Open scoutlytics.gg in <strong>Safari</strong> to add the Mobile App
            to your home screen
          </p>
        ) : null}

        {platform === 'generic' && !canPromptInstall ? (
          <p
            className="studio-mobile-app-generic"
            data-testid="studio-mobile-app-generic"
          >
            {STUDIO_COPY.mobileAppGeneric}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default StudioMobileAppModal;
